import { useCallback, useContext, useEffect, useRef, useState } from "react"
import { ToastContainer } from "react-toastify";
import axios from "./api/axios";
import { useLocation } from 'react-router-dom';
import "./App.scss";
import "react-toastify/dist/ReactToastify.css";
import { AuthContext } from "./contexts/AuthContext"
import { refreshTokenRoute } from "./api/routes";
import MainSiteContainer from "./containers/MainSiteContainer/MainSiteContainer";
import { pullInAccountSettings } from "./helpers/AccountTypes";
import { toast } from "react-toastify";
import { TOP_LEVEL_NAV_ROUTES } from "./config/NavRoutes";
import RightClickMenu from "./components/RightClickMenu/RightClickMenu";

// Location debugger component
const LocationDebugger = () => {
  const location = useLocation();
  
  useEffect(() => {
    console.log('Current location:', location);
    // Add a visible indicator on the page
    const debugElement = document.createElement('div');
    debugElement.style = 'position: fixed; bottom: 10px; right: 10px; background: rgba(0,0,0,0.7); color: white; padding: 10px; z-index: 9999; font-size: 14px; border-radius: 4px;';
    debugElement.textContent = `Path: ${location.pathname}${location.search || ''}`;
    document.body.appendChild(debugElement);
    
    return () => {
      if (document.body.contains(debugElement)) {
        document.body.removeChild(debugElement);
      }
    };
  }, [location]);
  
  return null;
};

function App() {
  const [authContext, setAuthContext] = useContext(AuthContext)
  const location = useLocation();
  const [pulledInConfigs, setPulledInConfigs] = useState(false);

  const verifyTimeout = useRef(null);

  //Handle refreshing of page to see if the token is still fresh and the user is still authenticated. This is designed to handle when the user refreshes the page
  const verifyUser = useCallback(() => {
    axios
    .post(refreshTokenRoute, {
    },{  withCredentials:true})
    .then(function (response) {
      //console.log('FAILED', response.failed)
      const data = response.data
      setAuthContext((oldValues) => {
        console.log('AUTH TOKEN', data.globalUserData.isConfirmed)
        axios.defaults.headers.common["Authorization"] = `Bearer ${data.token}`;
        return { ...oldValues,initialising: false, token:data.token, isConfirmed:data.globalUserData.isConfirmed, isLoggedIn:true, userData:data.globalUserData}
      })
    })
    .catch(function (error) {
      // Check if we are on any overlay/feature-style path (case-insensitive)
      const pathSegment = (location.pathname.split('/')[1] || '').toLowerCase();
      const featureSegment = (TOP_LEVEL_NAV_ROUTES.FEATURE.split('/')[1] || '').toLowerCase();

      // Treat these paths as overlay/preview contexts where we should not
      // force a full auth reset or redirect on refresh failure.
      const overlaySegments = ['obs-feature', 'feature', 'disruptor', 'csl'];
      const isOverlayPath = overlaySegments.includes(pathSegment) || pathSegment === featureSegment;

      console.log('Location debug info:', { 
        pathname: location.pathname,
        pathSegment,
        isOverlayPath,
        overlaySegments,
        featureRoute: TOP_LEVEL_NAV_ROUTES.FEATURE.split('/')[1]
      });

      // We only need to respond to the auth error if we are not on an overlay/feature page
      if (!isOverlayPath) {
        // Your existing login failure code
        const params = new URLSearchParams(window.location.search) 
        let twitchFail = params.get('failedTwitchLogin') 
        params.delete('failedTwitchLogin')
        window.history.replaceState(null, '', '?' + params + window.location.hash)

        if(twitchFail) {
          delete axios.defaults.headers.common["Authorization"];
          toast.error("Error: " + error);
        }

        console.log('FAILED',error)
        console.log('[KokoAPI] Refresh token error...');
        
        // Only reset auth context for non-feature pages
        setAuthContext(oldValues => {
          return { ...oldValues, initialising: false, token: null, isConfirmed:false, isLoggedIn:false, userData:{} }
        });
      } else {
        // For overlay/feature paths, allow preview mode to continue without authentication
        console.log('Overlay/feature path detected, continuing in preview mode despite auth failure');
        setAuthContext(oldValues => {
          return { ...oldValues, initialising: false, isPreviewMode: true }
        })
      }
    });
    clearTimeout(verifyTimeout.current);
    verifyTimeout.current = setTimeout(verifyUser, 5 * 60 * 1000)
  }, [setAuthContext])

  useEffect(() => {
    setTimeout(verifyUser, 20);
    // Remove no-transitions class after initial render
    setTimeout(() => {
      document.documentElement.classList.remove('no-transitions');
    }, 50);
  }, [verifyUser])

  useEffect(() => {
    if (pulledInConfigs === false) {
      pullInAccountSettings(1000, true);
      setPulledInConfigs(true);
    }
  }, [pulledInConfigs]);

  // Apply dark mode theme based on user preference
  useEffect(() => {
    const htmlElement = document.documentElement;

    const userPref = authContext.userData?.useDarkMode;

    // If userPref is explicitly boolean, honor it and persist
    if (typeof userPref === 'boolean') {
      if (userPref) {
        htmlElement.setAttribute('data-theme', 'dark');
        localStorage.setItem('icast-dark-mode', 'true');
        try { document.cookie = `icast-dark-mode=true;path=/;max-age=${60 * 60 * 24 * 365}`; } catch (e) {}
      } else {
        htmlElement.removeAttribute('data-theme');
        localStorage.setItem('icast-dark-mode', 'false');
        try { document.cookie = `icast-dark-mode=false;path=/;max-age=${60 * 60 * 24 * 365}`; } catch (e) {}
      }
    } else {
      // No explicit user preference (e.g., logged out) -> fall back to cookie/localStorage
      try {
        const cookieMatch = document.cookie.match(/(?:^|; )icast-dark-mode=([^;]+)/);
        const cookieVal = cookieMatch ? cookieMatch[1] : null;
        const saved = cookieVal ?? localStorage.getItem('icast-dark-mode');
        if (saved === 'true') {
          htmlElement.setAttribute('data-theme', 'dark');
        } else if (saved === 'false') {
          htmlElement.removeAttribute('data-theme');
        }
      } catch (e) {
        // ignore
      }
    }

    // Clear any inline background styles to let CSS custom properties take over
    htmlElement.style.backgroundColor = '';
    document.body.style.backgroundColor = '';
  }, [authContext.userData]);

  return (
      <div className="App">
        {/* <LocationDebugger /> {/* Add the debugger component here */}
        <MainSiteContainer></MainSiteContainer>
        <ToastContainer
          position="bottom-left"
          autoClose={3000}
          hideProgressBar={false}
          newestOnTop={true}
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
        />
        <RightClickMenu />
      </div>
  );
}

export default App;
